import { db, isDemoMode } from '../firebase';
import { collection, getDocs, doc, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import type { Question, FlashcardRecord, TestDefinition, TestAttempt } from '../types';
import { questionBank as staticQuestions } from '../data/questionBank';
import { validateQuestion, type ValidationError } from '../utils/validateQuestions';

const DEMO_QUESTIONS_KEY = 'demo_questions';
const DEMO_QUESTIONS_VERSION_KEY = 'demo_questions_version';
const DEMO_QUESTIONS_VERSION = 2; // Bump when static questionBank changes
const DEMO_FLASHCARDS_KEY = 'demo_flashcards';
const DEMO_TESTS_KEY = 'demo_tests';
const DEMO_ATTEMPTS_KEY = 'demo_attempts';

// ── Question CRUD ──

const getDemoQuestions = (): Question[] => {
  const storedVersion = localStorage.getItem(DEMO_QUESTIONS_VERSION_KEY);
  const currentVersion = parseInt(storedVersion || '0', 10);

  if (currentVersion >= DEMO_QUESTIONS_VERSION) {
    const stored = localStorage.getItem(DEMO_QUESTIONS_KEY);
    if (stored) {
      try { return JSON.parse(stored); } catch { /* fall through */ }
    }
  }

  // First load or version bump: re-seed from static data
  localStorage.setItem(DEMO_QUESTIONS_KEY, JSON.stringify(staticQuestions));
  localStorage.setItem(DEMO_QUESTIONS_VERSION_KEY, String(DEMO_QUESTIONS_VERSION));
  return [...staticQuestions];
};

const saveDemoQuestions = (questions: Question[]) => {
  localStorage.setItem(DEMO_QUESTIONS_KEY, JSON.stringify(questions));
};

export const questionService = {
  /** Get all questions. In demo mode uses localStorage; in prod loads from Firestore, falling back to static data. */
  async getAll(includeDeleted = false): Promise<Question[]> {
    if (isDemoMode) {
      const all = getDemoQuestions();
      return includeDeleted ? all : all.filter((q) => !q.deletedAt);
    }
    if (!db) return [...staticQuestions];
    try {
      const snap = await getDocs(collection(db, 'questions'));
      if (snap.empty) return [...staticQuestions]; // Not yet seeded to Firestore
      const all = snap.docs.map((d) => ({ ...d.data(), id: d.id } as Question));
      return includeDeleted ? all : all.filter((q) => !q.deletedAt);
    } catch (err) {
      console.error('Error loading questions:', err);
      return [...staticQuestions];
    }
  },

  /** Save (create or update) a single question. Returns validation errors if any. */
  async save(q: Question): Promise<ValidationError[]> {
    const errors = validateQuestion(q, new Set());
    if (errors.length) return errors;

    if (isDemoMode) {
      const all = getDemoQuestions();
      const idx = all.findIndex((existing) => existing.id === q.id);
      if (idx >= 0) all[idx] = q;
      else all.push(q);
      saveDemoQuestions(all);
      return [];
    }
    if (!db) return [];
    await setDoc(doc(db, 'questions', q.id), {
      topic: q.topic,
      difficulty: q.difficulty,
      question: q.question,
      choices: q.choices,
      correctAnswer: q.correctAnswer,
      explanation: q.explanation,
      isMath: q.isMath,
      linkedQualIds: q.linkedQualIds,
    });
    return [];
  },

  /** Soft-delete a question by id */
  async remove(questionId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoQuestions();
      const idx = all.findIndex((q) => q.id === questionId);
      if (idx >= 0) {
        all[idx].deletedAt = new Date();
        all[idx].deletedBy = deletedBy;
        saveDemoQuestions(all);
      }
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'questions', questionId), {
      deletedAt: Timestamp.now(),
      deletedBy,
    });
  },

  /** Bulk save — validates all, saves if all pass */
  async bulkSave(questions: Question[]): Promise<ValidationError[]> {
    const seenIds = new Set<string>();
    const allErrors: ValidationError[] = [];
    for (const q of questions) {
      allErrors.push(...validateQuestion(q, seenIds));
      seenIds.add(q.id);
    }
    if (allErrors.length) return allErrors;

    if (isDemoMode) {
      saveDemoQuestions(questions);
      return [];
    }
    if (!db) return [];
    // Save each doc
    for (const q of questions) {
      await setDoc(doc(db, 'questions', q.id), {
        topic: q.topic,
        difficulty: q.difficulty,
        question: q.question,
        choices: q.choices,
        correctAnswer: q.correctAnswer,
        explanation: q.explanation,
        isMath: q.isMath,
        linkedQualIds: q.linkedQualIds,
      });
    }
    return [];
  },

  /** Generate next available question id */
  nextId(existing: Question[]): string {
    let max = 0;
    for (const q of existing) {
      const num = parseInt(q.id.replace(/^q/, ''), 10);
      if (!isNaN(num) && num > max) max = num;
    }
    return `q${String(max + 1).padStart(3, '0')}`;
  },
};

// ── Flashcard Performance ──

const getDemoFlashcards = (userId: string): Record<string, FlashcardRecord> => {
  const stored = localStorage.getItem(`${DEMO_FLASHCARDS_KEY}_${userId}`);
  if (!stored) return {};
  try {
    const data = JSON.parse(stored);
    return Object.fromEntries(
      Object.entries(data).map(([k, v]: [string, any]) => [
        k,
        { ...v, lastSeen: new Date(v.lastSeen) },
      ])
    );
  } catch {
    return {};
  }
};

const saveDemoFlashcards = (userId: string, records: Record<string, FlashcardRecord>) => {
  localStorage.setItem(`${DEMO_FLASHCARDS_KEY}_${userId}`, JSON.stringify(records));
};

export const flashcardService = {
  async getRecords(userId: string): Promise<Record<string, FlashcardRecord>> {
    if (isDemoMode) return getDemoFlashcards(userId);
    if (!db) return {};
    try {
      const snap = await getDocs(collection(db, 'flashcards', userId, 'records'));
      const records: Record<string, FlashcardRecord> = {};
      snap.docs.forEach((d) => {
        const data = d.data();
        records[d.id] = {
          questionId: d.id,
          correct: data.correct || 0,
          incorrect: data.incorrect || 0,
          lastSeen: data.lastSeen?.toDate() || new Date(),
          streak: data.streak || 0,
        };
      });
      return records;
    } catch (err) {
      console.error('Error loading flashcard records:', err);
      return {};
    }
  },

  async saveRecord(userId: string, record: FlashcardRecord): Promise<void> {
    if (isDemoMode) {
      const all = getDemoFlashcards(userId);
      all[record.questionId] = record;
      saveDemoFlashcards(userId, all);
      return;
    }
    if (!db) return;
    await setDoc(doc(db, 'flashcards', userId, 'records', record.questionId), {
      correct: record.correct,
      incorrect: record.incorrect,
      lastSeen: record.lastSeen,
      streak: record.streak,
    });
  },
};

// ── Test Definitions ──

const getDemoTests = (): TestDefinition[] => {
  const stored = localStorage.getItem(DEMO_TESTS_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored).map((t: any) => ({
      ...t,
      createdAt: new Date(t.createdAt),
    }));
  } catch {
    return [];
  }
};

const saveDemoTests = (tests: TestDefinition[]) => {
  localStorage.setItem(DEMO_TESTS_KEY, JSON.stringify(tests));
};

export const testService = {
  async getAll(includeDeleted = false): Promise<TestDefinition[]> {
    if (isDemoMode) {
      const all = getDemoTests();
      return includeDeleted ? all : all.filter((t) => !t.deletedAt);
    }
    if (!db) return [];
    try {
      const snap = await getDocs(collection(db, 'tests'));
      const all = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.title,
          description: data.description || '',
          questionIds: data.questionIds || [],
          createdAt: data.createdAt?.toDate() || new Date(),
          createdBy: data.createdBy,
          createdByName: data.createdByName || '',
          timeLimit: data.timeLimit ?? null,
          deletedAt: data.deletedAt?.toDate() || null,
          deletedBy: data.deletedBy || null,
        };
      });
      return includeDeleted ? all : all.filter((t) => !t.deletedAt);
    } catch (err) {
      console.error('Error loading tests:', err);
      return [];
    }
  },

  async save(test: TestDefinition): Promise<void> {
    if (isDemoMode) {
      const all = getDemoTests();
      const idx = all.findIndex((t) => t.id === test.id);
      if (idx >= 0) all[idx] = test;
      else all.push(test);
      saveDemoTests(all);
      return;
    }
    if (!db) return;
    await setDoc(doc(db, 'tests', test.id), {
      title: test.title,
      description: test.description,
      questionIds: test.questionIds,
      createdAt: test.createdAt,
      createdBy: test.createdBy,
      createdByName: test.createdByName,
      timeLimit: test.timeLimit,
    });
  },

  async remove(testId: string, deletedBy: string): Promise<void> {
    if (isDemoMode) {
      const all = getDemoTests();
      const idx = all.findIndex((t) => t.id === testId);
      if (idx >= 0) {
        all[idx].deletedAt = new Date();
        all[idx].deletedBy = deletedBy;
        saveDemoTests(all);
      }
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'tests', testId), {
      deletedAt: Timestamp.now(),
      deletedBy,
    });
  },
};

// ── Test Attempts ──

const getDemoAttempts = (): TestAttempt[] => {
  const stored = localStorage.getItem(DEMO_ATTEMPTS_KEY);
  if (!stored) return [];
  try {
    return JSON.parse(stored).map((a: any) => ({
      ...a,
      startedAt: new Date(a.startedAt),
      completedAt: new Date(a.completedAt),
    }));
  } catch {
    return [];
  }
};

const saveDemoAttempts = (attempts: TestAttempt[]) => {
  localStorage.setItem(DEMO_ATTEMPTS_KEY, JSON.stringify(attempts));
};

export const attemptService = {
  async getByUser(userId: string): Promise<TestAttempt[]> {
    if (isDemoMode) return getDemoAttempts().filter((a) => a.userId === userId);
    if (!db) return [];
    try {
      const snap = await getDocs(collection(db, 'testAttempts'));
      return snap.docs
        .map((d) => {
          const data = d.data();
          return {
            id: d.id,
            testId: data.testId,
            testTitle: data.testTitle || '',
            userId: data.userId,
            userName: data.userName || '',
            answers: data.answers || {},
            score: data.score || 0,
            correctCount: data.correctCount || 0,
            totalCount: data.totalCount || 0,
            startedAt: data.startedAt?.toDate() || new Date(),
            completedAt: data.completedAt?.toDate() || new Date(),
          };
        })
        .filter((a) => a.userId === userId);
    } catch (err) {
      console.error('Error loading attempts:', err);
      return [];
    }
  },

  async getAll(): Promise<TestAttempt[]> {
    if (isDemoMode) return getDemoAttempts();
    if (!db) return [];
    try {
      const snap = await getDocs(collection(db, 'testAttempts'));
      return snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          testId: data.testId,
          testTitle: data.testTitle || '',
          userId: data.userId,
          userName: data.userName || '',
          answers: data.answers || {},
          score: data.score || 0,
          correctCount: data.correctCount || 0,
          totalCount: data.totalCount || 0,
          startedAt: data.startedAt?.toDate() || new Date(),
          completedAt: data.completedAt?.toDate() || new Date(),
        };
      });
    } catch (err) {
      console.error('Error loading all attempts:', err);
      return [];
    }
  },

  async save(attempt: TestAttempt): Promise<void> {
    if (isDemoMode) {
      const all = getDemoAttempts();
      all.push(attempt);
      saveDemoAttempts(all);
      return;
    }
    if (!db) return;
    await setDoc(doc(db, 'testAttempts', attempt.id), {
      testId: attempt.testId,
      testTitle: attempt.testTitle,
      userId: attempt.userId,
      userName: attempt.userName,
      answers: attempt.answers,
      score: attempt.score,
      correctCount: attempt.correctCount,
      totalCount: attempt.totalCount,
      startedAt: attempt.startedAt,
      completedAt: attempt.completedAt,
    });
  },
};
